/* @flow */
import {
  observable,
  action,
  computed,
  decorate,
  flow
} from 'mobx'
import stringSimilarity from 'string-similarity'
import { TOPSONG_COUNT, PRELOAD_TRACKS_COUNT, SONG_COUNT } from './playlist.config'
import { FreeTrialMessages } from './freeTrial'
import { buildSchedule, channelRules } from './playlistSchedule'
import { Rotation } from './audioRotation'

// Songs played in the last few hours are remembered (on this device), so the
// same song doesn't come round again within the hour.
const RECENT_KEY = 'gymradio_recent_songs'
const RECENT_HOURS = 3
const RECENT_MAX = 150
const SONG_PICK = 20 // songs to choose from when adding one (the server allows up to 20)
const isMusic = (t) => !!t && (t.type === 'song' || t.type === 'topsong')
const readRecent = (hours = RECENT_HOURS) => {
  try {
    const cutoff = Date.now() - hours * 3600 * 1000
    return JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]').filter((r) => r && r.t > cutoff)
  } catch (e) {
    return []
  }
}
import { ClassAnnouncer } from './classAnnouncements'
import { isAdvert } from '../helpers/trackLabel'

const PLAY_GYM_NAME_AFTER_MESSAGES = false

// Old 2023 "FREE TRIAL ..." recordings are filed with the jingles; they must
// never play as a jingle (paying gyms would hear them). Free-trial messages
// have their own slot.
const onlyJingles = (tracks) => (tracks || []).filter((t) => !/free\s*trial/i.test(`${t.title || ''} ${t.key || ''}`))

type PlaylistSchemaType = 'promotion' | 'message' | 'motivation' | 'topsong' | 'song' | 'decade' | 'freetrial'

export default class PlaylistStore {
  api
  userStore
  tracks = []
  userStore
  audioMessageStore
  isLoading = true
  playlistSchema = []
  counter = 0
  playlistService = null
  adFrequency = 'normal' // light | normal | busy (Ads panel)
  freeTrialMessages = new FreeTrialMessages()
  classAnnouncer = new ClassAnnouncer()
  rules = {} // per-channel music rules (playlistSchedule.js CHANNEL_RULES)

  // How long a song of this kind is kept out after it plays
  recentHoursFor (trackType) {
    return (trackType === 'topsong' && this.rules.topSongRecentHours) || RECENT_HOURS
  }

  constructor (api, userStore, audioMessageStore) {
    this.api = api
    this.userStore = userStore
    this.audioMessageStore = audioMessageStore

    // Jingles, quotes and the gym's ads each rotate evenly: all of them play
    // once (shuffled) before any repeats
    const POOL = 20 // the server allows at most 20 per request
    this.adRotation = new Rotation(async () => {
      const { id, franchiseId, isPremium } = this.userStore
      return this.promotionalMessagesEnabled
        ? this.api.fetchRandomAudioMessages(id, franchiseId, POOL, isPremium)
        : []
    })
    this.jingleRotation = new Rotation(async () => {
      const { id, isPremium } = this.userStore
      return onlyJingles(await this.api.listRandom(['gymradioads'], id, 'promotion', POOL, isPremium))
    })
    this.motivationRotation = new Rotation(async () => {
      const { id, isPremium } = this.userStore
      return this.api.listRandom([], id, 'motivation', POOL, isPremium)
    })
  }

  // Remember a song once it has played (or been skipped)
  rememberPlayed (track) {
    if (!isMusic(track)) return
    try {
      const recent = readRecent()
      recent.push({ u: track.uuid, n: track.fullName, t: Date.now() })
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(-RECENT_MAX)))
    } catch (e) {}
  }

  isRecent (song, recent = readRecent()) {
    if (!song) return false
    const byName = this.rules.matchByName !== false
    return recent.some((r) => r.u === song.uuid ||
      (byName && r.n && song.fullName && stringSimilarity.compareTwoStrings(r.n, song.fullName) >= 0.8))
  }

  recentSongIds () {
    return readRecent().map((r) => r.u).filter(Boolean)
  }

  get currentTrack () {
    return (this.tracks.length > 0 && this.tracks[0]) || {}
  }

  get maxTrackCount () {
    return this.playlistSchema.length
  }

  get position () {
    return this.counter % this.maxTrackCount
  }

  set position (position: number) {
    this.counter = position
  }

  get upcomingPromotionalMessages () {
    return this.tracks.filter((track) => {
      return track.type === 'message'
    })
  }

  get promotionalMessagesEnabled () {
    const { isPremium } = this.userStore
    const { enabledCount } = this.audioMessageStore
    return isPremium && enabledCount > 0
  }

  // Signed-in accounts that haven't subscribed (trial running or ended)
  // hear "sign up at GymRadio.com" messages between songs.
  get freeTrialMessagesEnabled () {
    const { isSubscribed, isAuthenticated } = this.userStore
    return !!(isAuthenticated && !isSubscribed)
  }

  get adsEnabled () {
    const { isPremium } = this.userStore
    return !isPremium
  }

  get motivationalMessagesEnabled () {
    const { disableMotivationQuotes } = this.userStore
    return !disableMotivationQuotes
  }

  get explicitFilter () {
    const { explicitFilter } = this.userStore
    return explicitFilter
  }

  getTagSelector (tags: string[]) {
    let explicitTags = tags
    switch (this.explicitFilter) {
      case 'soft':
        explicitTags = tags.map((tag) => {
          return [
            `${tag},softexplicit`,
            `${tag},clean`
          ]
        })
        break
      case 'clean':
        explicitTags = tags.map((tag) => {
          return `${tag},clean`
        })
        break
    }
    return explicitTags
  }

  dislikeSong (uuid) {
    const { id: userId } = this.userStore
    if (userId) {
      this.api.dislikeSong(userId, uuid)
    }
  }

  loadPlaylist = flow(function * (tags: string[], playing: boolean) {
    const { id: userId, franchiseId, isPremium } = this.userStore
    this.rules = channelRules(tags)

    this.isLoading = true

    // Keep current track if playing
    const shouldKeepPlayingSong = playing && this.tracks.length > 0
    if (shouldKeepPlayingSong) {
      this.tracks.replace([...this.tracks.slice(0, 1)])
      this.position = 1
    } else {
      this.tracks.replace([])
      this.position = 0
    }

    // The gym's ads may have been switched on/off: start their rotation afresh.
    // Peek at each pool to see which kinds of audio item there are.
    this.adRotation.reset()
    const firstAd = yield this.adRotation.next()
    this.adRotation.putBack(firstAd)
    const firstJingle = yield this.jingleRotation.next()
    this.jingleRotation.putBack(firstJingle)
    const firstQuote = this.motivationalMessagesEnabled ? yield this.motivationRotation.next() : null
    this.motivationRotation.putBack(firstQuote)

    // leave out anything played in the last few hours
    const recent = readRecent()
    const recentTop = readRecent(this.recentHoursFor('topsong'))
    const topSongs = (yield this.api.listRandom(this.getTagSelector(tags), userId, 'topsong', TOPSONG_COUNT, isPremium))
      .filter((t) => !this.isRecent(t, recentTop))
    const songs = (yield this.api.listRandom(this.getTagSelector(tags), userId, 'song', SONG_COUNT, isPremium))
      .filter((t) => !this.isRecent(t, recent))
    // a second pool on some channels (Lite: 90s hits)
    const decadeSongs = this.rules.decadeTags
      ? (yield this.api.listRandom(this.getTagSelector(this.rules.decadeTags), userId, 'song', SONG_COUNT, isPremium))
        .filter((t) => !this.isRecent(t, recent))
      : []

    // Compute 
    const messagesEnabled = !!firstAd
    const promotionalMessagesEnabled = !!firstJingle
    const motivationalMessagesEnabled = this.motivationalMessagesEnabled && !!firstQuote
    const topSongsEnabled = topSongs.length > 0
    const decadeEnabled = decadeSongs.length > 0

    // Create playlist
    const playlistSchema = yield this._buildPlaylistSchema(
      messagesEnabled,
      promotionalMessagesEnabled,
      motivationalMessagesEnabled,
      topSongsEnabled,
      decadeEnabled
    )

    this.playlistSchema = playlistSchema

    const loadUntil = Math.min(playlistSchema.length, this.position + PRELOAD_TRACKS_COUNT)
    let i = this.position
    for (; i < loadUntil; i++) {
      if (playlistSchema[i]) {
        const type: PlaylistSchemaType = playlistSchema[i]
        switch (type) {
          case 'message':
            yield this._addPromotion(yield this.adRotation.next(), userId, franchiseId, isPremium)
            break
          case 'promotion':
            yield this._add(yield this.jingleRotation.next(), false, isPremium)
            break
          case 'motivation':
            yield this._addPromotion(yield this.motivationRotation.next(), userId, franchiseId, isPremium)
            break
          case 'topsong':
            yield this._add(topSongs.shift(), false, isPremium)
            break
          case 'decade':
            yield this._add(decadeSongs.shift() || songs.shift(), false, isPremium)
            break
          case 'freetrial':
            yield this._add(this.freeTrialMessages.next(), false, isPremium)
            break
          default:
            yield this._add(songs.shift(), false, isPremium)
            break
        }
      }
    }
    // carry on from here as songs play
    this.position = i

    this.isLoading = false
  })

  removeTrack = flow(function * (track, tags) {
    const trackIndex = this.tracks
      .findIndex((upcomingTrack) => {
        return upcomingTrack.id === track.id
      })

    if (trackIndex >= 0) {
      this.tracks.splice(trackIndex, 1) // remove element
      yield this.addTracks(this.getTagSelector(tags), 1)
    }
  })

  addTracks = flow(function * (tags, count) {
    const { id: userId, franchiseId, isPremium } = this.userStore
    const MAX_RETRIES = 5

    for (let i = 0; i < count; i++) {
      if (this.playlistSchema[this.position]) {
        const trackType: PlaylistSchemaType = this.playlistSchema[this.position]
        let newTracks = []
        switch (trackType) {
          case 'message':
            newTracks = [yield this.adRotation.next()].filter(Boolean)
            break
          case 'promotion':
            newTracks = [yield this.jingleRotation.next()].filter(Boolean)
            break
          case 'motivation':
            newTracks = [yield this.motivationRotation.next()].filter(Boolean)
            break
          case 'topsong':
            newTracks = yield this.api.listRandom(tags, userId, 'topsong', SONG_PICK, isPremium)
            break
          case 'decade':
            newTracks = this.rules.decadeTags
              ? yield this.api.listRandom(this.getTagSelector(this.rules.decadeTags), userId, 'song', SONG_PICK, isPremium)
              : []
            break
          case 'freetrial':
            newTracks = [this.freeTrialMessages.next()]
            break
          default:
            newTracks = yield this.api.listRandom(tags, userId, 'song', SONG_PICK, isPremium)
            break
        }

        // An audio item couldn't be fetched: keep the gap an audio item anyway
        // (free-trial message, or a jingle), so it's never two songs without one
        const isAudioSlot = ['message', 'promotion', 'motivation', 'freetrial'].indexOf(trackType) >= 0
        if (newTracks.length === 0 && isAudioSlot) {
          if (this.freeTrialMessagesEnabled) {
            newTracks = [this.freeTrialMessages.next()]
          } else if (trackType !== 'promotion') {
            newTracks = [yield this.jingleRotation.next()].filter(Boolean)
          }
        }

        // Try to fetch songs as a fallback
        if (newTracks.length === 0) {
          newTracks = yield this.api.listRandom(this.getTagSelector(tags), userId, 'song', MAX_RETRIES, isPremium)
        }

        // Throw exception if offline or api down
        if (newTracks.length === 0) {
          throw new Error('Cannot add any tracks')
        }

        // Pick the first track that isn't lined up already or played recently
        const recentNow = readRecent(this.recentHoursFor(trackType))
        for (let j = 0; j < newTracks.length; j++) {
          const newTrack = newTracks[j]
          // Is fetched track in playlist?
          const isTrackInUpcoming = this.tracks
            .filter((track) => {
              // Check uuid
              const containUuid = newTrack.uuid === track.uuid
              if (containUuid) {
                return true
              }

              // Check word similarity (not on channels where many tracks share a name, e.g. Spa)
              if (this.rules.matchByName === false) return false
              const similarity = stringSimilarity.compareTwoStrings(track.fullName, newTrack.fullName)

              const isSimilar = similarity >= 0.8
              return isSimilar
            }).length > 0

          // ...or played in the last few hours
          const isRepeat = isTrackInUpcoming ||
            ((trackType === 'song' || trackType === 'topsong' || trackType === 'decade') && this.isRecent(newTrack, recentNow))
          if (isRepeat && j < newTracks.length - 1) {
            // Track is in the playlist => continue
            continue
          }

          yield this._addPromotion(newTrack, userId, franchiseId, isPremium)
          this.counter++
          break // Track was added => break
        }
      }
    }
  })

  _isNextTrackLoading = false
  nextTrack = flow(function * (tags: string[]) {
    const { isPremium } = this.userStore
    const removedSong = this.tracks.shift()
    this.rememberPlayed(removedSong)

    // A class starting soon? Its announcement plays now, ahead of any ad,
    // and an ad that was next is dropped so two never play back to back.
    if (!this.rules.noTalk && !(removedSong && removedSong.viewType === 'classannouncement')) {
      const announcement = this.classAnnouncer.next()
      if (announcement) {
        if (this.tracks.length > 0 && isAdvert(this.tracks[0])) this.tracks.shift()
        const track = new Track(this, announcement.uuid)
        track.updateFromJson(announcement)
        this.tracks.unshift(track)
      }
    }

    if (this._isNextTrackInProgress) {
      return
    }

    // Add at least one new track
    const count = Math.max(
      1,
      PRELOAD_TRACKS_COUNT - this.tracks.length
    )
    this._isNextTrackInProgress = true
    try {
      yield this.addTracks(this.getTagSelector(tags), count)
    } catch (err) {
      // Could not push new track
      // Add it after last preloaded track
      // Available only for premium users
      if (isPremium) {
        const index = this.tracks.findIndex((track) => track.loaded === false)
        this.tracks.splice(index, 0, removedSong)
      }
    } finally {
      // This is not necessary, but I want to be 100% sure, that _isNextTrackInProgress will be false
      this._isNextTrackInProgress = false
    }

    this._isNextTrackInProgress = false
    return
  })

  addTrackByUuid = flow(function * (uuid: string, next = false) {
    const { isPremium } = this.userStore
    const track = yield this.api.getByUuid(uuid, isPremium)

    if (track) {
      this._add(track, next, isPremium)
    }
  })

  // Commercials: the gym's own ads that are switched on (Pro / trial only).
  // GymRadio.com jingles have their own slot, so they're not used here any more.
  async _getPromotionalMessages (userId: string, franchiseId: string, count: number, isPremium = true) {
    let promotionalMessages = []
    if (this.promotionalMessagesEnabled) {
      promotionalMessages = this._fillArray(
        await this.api.fetchRandomAudioMessages(userId, franchiseId, count, isPremium), count
      )
    }
    return promotionalMessages
  }

  // Where audio items go between songs: see playlistSchedule.js for the rules.
  async _buildPlaylistSchema (messagesEnabled, promotionalMessagesEnabled, motivationalMessagesEnabled, topSongsEnabled, decadeEnabled = false) {
    const r = this.rules || {}
    if (r.noTalk) return buildSchedule({ topSongs: topSongsEnabled, topSongEvery: r.topSongEvery }) // music only
    const motivation = motivationalMessagesEnabled && r.motivation !== false
    return buildSchedule({
      frequency: this.adFrequency || 'normal',
      commercials: messagesEnabled,
      freeTrial: this.freeTrialMessagesEnabled,
      jingles: promotionalMessagesEnabled,
      motivation,
      topSongs: topSongsEnabled,
      topSongEvery: r.topSongEvery,
      decadeEvery: decadeEnabled ? r.decadeEvery || 0 : 0,
      decadeOffset: r.decadeOffset || 0,
      minSongsBetween: r.minSongsBetween,
      // never two songs without an audio item: fill the gap (not on channels
      // that only talk when something is due, e.g. Lite)
      fillWith: r.fill === false ? null
        : this.freeTrialMessagesEnabled ? 'freetrial'
          : promotionalMessagesEnabled ? 'promotion'
            : motivation ? 'motivation' : null
    })
  }

  _extractMetadata (state: any) {
    return state?.meta[Object.keys(state?.meta)[0]] || {}
  }

  _fillArray (items: Array<any>, count: number) {
    const length = items.length
    const filledItems = [...items]
    if (length < count && length > 0) {
      for (let i = length; i < count; i++) {
        const randomItem = items[Math.floor(Math.random() * length)] // get random item from array
        filledItems.push(randomItem)
      }
    }

    return filledItems
  }

  _addPromotion = flow(function * (track, userId, franchiseId, isPremium) {
    if (!track) {
      return
    }

    yield this._add(track, false, isPremium)

    // The gym-name clip used to follow a message or quote. It's off for now
    // (never two audio items back to back); it may come back with per-gym branding.
    if (!PLAY_GYM_NAME_AFTER_MESSAGES) return

    const isPromotionalMessage =  (
      track.type === 'message' &&
      track.tags.indexOf('businessmessages') >= 0
    )
    const isMotivationalMessage = (
      track.type === 'motivation'
    )
    const shouldAddGymName = isPromotionalMessage || isMotivationalMessage

    if (shouldAddGymName) {
      const {
        title,
        artist
      } = track
      const promotions = yield this.api.fetchGymName(userId, franchiseId, isPremium)

      if (promotions.length > 0) {
        const promotion = promotions[0]

        // Do not add gymradio name to promotional messages
        const isGymRadioName = promotion.tags.indexOf('gymradio') >= 0

        if (isPromotionalMessage && isGymRadioName) {
          return
        }

        const viewType = `${promotion.type}-${track.type}`
        yield this._add({
          ...promotion,
          viewType,
          title,
          artist
        }, false, isPremium)
      }
    }
  })

  _add (json, next = false, isPremium = true) {
    if (!json) {
      return
    }
    let track = new Track(this, json.uuid)
    track.updateFromJson(json)

    if (!next) {
      this.tracks.push(track)
    } else {
      // Add as next playing song

      // If track on index 1 is type === gymname,
      // insert on the next position
      const index = (this.tracks[1] && this.tracks[1].type === 'gymname') ? 2 : 1
      this.tracks.splice(index, 0, track)
    }
  }
}

decorate(PlaylistStore, {
  adFrequency: observable,
  tracks: observable,
  promotionalMessagesEnabled: computed,
  motivationalMessagesEnabled: computed,
  adsEnabled: computed,
  freeTrialMessagesEnabled: computed,
  upcomingPromotionalMessages: computed,
  currentTrack: computed,
  _add: action,
  isLoading: observable
})

export class Track {
  uuid = ''
  artist = ''
  title = ''
  url = ''
  type = ''
  viewType = ''
  id = ''
  gaiaId: ''
  isrc: ''
  preloaded = false
  downloaded = false

  store = undefined

  constructor (store, uuid) {
    this.store = store
    this.uuid = uuid
  }

  updateFromJson (json) {
    this.uuid = json.uuid || ''
    this.artist = json.artist || ''
    this.title = json.title || ''
    this.type = json.type || ''
    this.gaiaId = json.gaiaId || ''
    this.isrc = json.isrc || ''
    this.viewType = json.viewType || json.type || ''
    this.url = json.url || ''
    this.id = `${json.uuid}${Date.now()}${Math.random()}`
  }

  get fullName () {
    return `${this.artist} - ${this.title}`
  }

  get asJson () {
    return {
      uuid: this.uuid,
      url: this.url,
      artist: this.artist,
      title: this.title,
      type: this.type,
      viewType: this.viewType
    }
  }
}

decorate(Track, {
  uuid: observable,
  url: observable,
  artist: observable,
  title: observable,
  type: observable,
  id: observable,
  preloaded: observable,
  downloaded: observable,
  fullName: computed,
  asJson: computed
})
